import { getCodeChallenge, getCodeVerifier } from '../../../../modules/auth/utils';
import { getBaseRedirectUrl } from '../../../../utils/base-url';
import { stringifyFullUrl } from '../../../../utils/url-params';
import { AccessToken } from '../../../access-token';
import { ApiCall } from '../../../http-service/types/ApiCall';
import { LoginUrlParams, SSOService } from '../../SSOService';
import { ImpersonateParams } from '../../types/ImpersonateParams';
import { UserImpersonation } from '../../../user-impersonation';
import { B2CRefreshTokenExchangeParams } from './api/types/B2CRefreshTokenExchangeParams';
import { B2CAuthTokenExchangeParams } from './api/types/B2CAuthTokenExchangeParams';
import { B2C_SSO_POLICY } from './api/enums/b2c-sso-policy';
import { exchangeB2CToken, logoutB2C } from './api';
import { B2C_BASE_URL, B2C_CALLBACK_ROUTE_URL } from './const';

class B2CSSOAuthService implements SSOService {
  baseUrl: string;
  routeUrl: string;

  constructor() {
    this.baseUrl = B2C_BASE_URL;
    this.routeUrl = B2C_CALLBACK_ROUTE_URL;
  }

  private async getBaseUrlQueryParams() {
    const baseRedirectUrl = getBaseRedirectUrl();
    const codeChallenge = await getCodeChallenge();

    return {
      client_id: process.env.AZURE_AD_B2C__FRONTEND_CLIENT_ID as string,
      response_type: 'code',
      response_mode: 'query',
      scope: 'https://graph.microsoft.com/.default',
      redirect_uri: `${baseRedirectUrl}/app/sign-in/${this.getRouteUrl()}`,
      code_challenge: codeChallenge,
      code_challenge_method: 'S256',
    };
  }

  private getBaseTokenExchangeParams() {
    if (!this.isAvailable()) return null;
    return {
      client_id: process.env.AZURE_AD_B2C__FRONTEND_CLIENT_ID,
      scope: `openid profile offline_access https://${process.env.AZURE_AD_B2C__TENANT}.onmicrosoft.com/api/${process.env.AZURE_AD_B2C__SCOPE}.read https://${process.env.AZURE_AD_B2C__TENANT}.onmicrosoft.com/api/${process.env.AZURE_AD_B2C__SCOPE}.write`,
    };
  }

  getRouteUrl() {
    return this.routeUrl;
  }

  getBaseUrl() {
    return this.baseUrl;
  }

  isAvailable() {
    return !!(process.env.AZURE_AD_B2C__FRONTEND_CLIENT_ID &&
      process.env.AZURE_AD_B2C__TENANT &&
      process.env.AZURE_AD_B2C__SCOPE);
  }

  public async getLoginUrl(
    params?: LoginUrlParams,
  ) {

    const baseQueryParams = await this.getBaseUrlQueryParams();

    return stringifyFullUrl({
      url: `${this.baseUrl}/${B2C_SSO_POLICY.SIGN_IN}/oauth2/v2.0/authorize`,
      query: {
        ...baseQueryParams,
        state: params?.state || null,
        domain_hint: params?.ssoDomain || null,
      }
    });
  }

  public async getImpersonateUrl(params: ImpersonateParams) {
    const baseQueryParams = await this.getBaseUrlQueryParams();
    return stringifyFullUrl({
      url: `${this.baseUrl}/${B2C_SSO_POLICY.IMPERSONATION}/oauth2/v2.0/authorize`,
      query: {
        ...baseQueryParams,
        ...params,
      }
    });
  }

  public getAuthTokenApiCall(code: string) {
    const impersonationData = UserImpersonation.getImpersonationData();

    const params: B2CAuthTokenExchangeParams = {
      ...this.getBaseTokenExchangeParams(),
      grant_type: 'authorization_code',
      code_verifier: getCodeVerifier(),
      policy: impersonationData ? B2C_SSO_POLICY.IMPERSONATION : B2C_SSO_POLICY.SIGN_IN,
      code,
    };

    const apiCall: ApiCall = ({ abortSignal }) => {
      return exchangeB2CToken({ params, abortSignal });
    };
    return apiCall;
  }

  public getRefreshTokenApiCall() {
    const params: B2CRefreshTokenExchangeParams = {
      ...this.getBaseTokenExchangeParams(),
      grant_type: 'refresh_token',
      refresh_token: AccessToken.getRefreshToken()!,
      access_token: AccessToken.getToken()!,
      policy: B2C_SSO_POLICY.SIGN_IN
    };

    const apiCall: ApiCall = ({ abortSignal }) => {
      return exchangeB2CToken({ params, abortSignal });
    };

    return apiCall;
  }

  public getLogoutApi() {
    const apiCall: ApiCall = ({ abortSignal }) => {
      return logoutB2C({ params: undefined, abortSignal });
    };
    return apiCall;
  }
}

export const b2cSSOService = new B2CSSOAuthService(); 